import { Button } from '@nextui-org/react';
import { ArrowRight, ChevronLeft, ChevronRight } from 'lucide-react';
import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import Card from './card';
import Link from 'next/link';

export const ProductSlider = ({
  products,
  title,
  link,
}: {
  products: any[];
  title: string;
  link?: string;
}) => {
  const [index, setIndex] = useState(0);
  const [itemsPerSlide, setItemsPerSlide] = useState(2);
  const totalSlides = Math.ceil(products.length / itemsPerSlide);

  // Update items per slide based on screen size
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth < 768) {
        setItemsPerSlide(2);
      } else if (window.innerWidth < 1024) {
        setItemsPerSlide(3);
      } else {
        setItemsPerSlide(5);
      }
    };

    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const nextSlide = () =>
    setIndex((prevIndex) => (prevIndex + 1) % totalSlides);
  const prevSlide = () =>
    setIndex((prevIndex) => (prevIndex - 1 + totalSlides) % totalSlides);

  return (
    <div className="relative mb-8 w-full">
      <div className="mb-3 flex items-center justify-between gap-3 px-4 md:px-0">
        {title && (
          <h2 className="min-w-0 truncate text-base font-bold md:text-lg lg:text-2xl">
            {title}
          </h2>
        )}
        {link && (
          <Button
            href={link}
            as={Link}
            variant="light"
            disableRipple
            className="flex shrink-0 items-center gap-1 text-sm text-brand hover:!bg-transparent hover:text-brand/80"
          >
            View All
            <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
          </Button>
        )}
      </div>
      <div className="relative">
        {index > 0 && (
          <Button
            variant="solid"
            size="md"
            className="absolute left-0 top-1/2 z-10 h-8 w-8 min-w-0 -translate-y-1/2 rounded-full bg-white p-0 shadow-lg hover:bg-gray-50 dark:bg-gray-800 dark:hover:bg-gray-700 md:h-10 md:w-10"
            onClick={prevSlide}
          >
            <ChevronLeft className="h-4 w-4" />
          </Button>
        )}

        <div className="overflow-hidden pb-2">
          <motion.div
            className="flex duration-300 ease-in-out"
            animate={{ x: `-${index * 100}%` }}
            transition={{ type: 'tween', ease: 'easeInOut' }}
          >
            {Array.from({ length: totalSlides }).map((_, slideIndex) => (
              <div key={slideIndex} className="w-full flex-shrink-0">
                <div className="grid w-full grid-cols-2 gap-3 px-4 md:grid-cols-3 md:gap-4 md:px-12 lg:grid-cols-5">
                  {products
                    .slice(
                      slideIndex * itemsPerSlide,
                      (slideIndex + 1) * itemsPerSlide
                    )
                    .map((product) => (
                      <Card key={product.id} product={product} />
                    ))}
                </div>
              </div>
            ))}
          </motion.div>
        </div>

        {index < totalSlides - 1 && (
          <Button
            variant="solid"
            size="md"
            className="absolute right-0 top-1/2 z-10 h-8 w-8 min-w-0 -translate-y-1/2 rounded-full bg-white p-0 shadow-lg hover:bg-gray-50 dark:bg-gray-800 dark:hover:bg-gray-700 md:h-10 md:w-10"
            onClick={nextSlide}
          >
            <ChevronRight className="h-4 w-4" />
          </Button>
        )}
      </div>
    </div>
  );
};

export default ProductSlider;
